import React from "react";
import { Alert } from 'react-bootstrap'
import QuoteStepBase from '../../components/QuoteStepBase'
import ButtonContinue from "../../components/ButtonContinue";
import VehicleCoverages from "./VehicleCoverages";
import { IQuoteStepState, IQuoteStepComponentProps } from "../../components/interfaces";
import { QuoteStep } from "./QuoteStepLayout";
import { connect } from "react-redux";
import { FULL_QUOTE_STEPS, COMPONENTS } from "../../AppConstants";
import { IAppState } from "../../state/interfaces";
import { IBindQuoteResult, IExternalQuote, IMvrResult } from "../../classes/ExternalQuote";
import Utilities from "../../classes/Utilities";
import { ICoverageConfig, IPaymentChoice, IQuoteApiClient, ILOBApiClient, IPaths } from "../../classes/Domain";
import FullQuoteActions, { IBindQuoteResponse } from "../../state/fullQuoteActions";
import Calendar from "../../components/Calendar";
import moment from 'moment-timezone';
import { validationUtils } from "../../validation/Validation";
import QuoteActions from "../../state/appActions";


const today = new Date();

interface IState extends IQuoteStepState {
    vehicleChanged: boolean,
    coveragesChanged: boolean,
    driverChanged: boolean,
    violationsChanged: boolean,
    paymentChoice: IPaymentChoice | null,
    bindQuoteResult: IBindQuoteResult | null,
    externalQuote: IExternalQuote,
    rerating: boolean,
    effDateChanging: boolean,
    effectiveDatetimeStamp: string;
}

interface IProps extends IQuoteStepComponentProps {
    externalQuote: IExternalQuote,
    coverageConfig: ICoverageConfig,
    paymentChoice: IPaymentChoice,
    mvrResult: IMvrResult,
    bindQuote: (quoteId: number) => Promise<any>,
    resetChangedFlags: () => void,
    reSetQuoteEffDate: (quoteId: number, paymentPlanCode: number, effectiveDate: string, effectiveDatetimeStamp: string) => Promise<any>,
    reSetQuoteTimeStampEffDate: (effectiveDatetimeStamp: string) => Promise<any>,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class PolicyDetails extends QuoteStepBase<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.next = this.next.bind(this);
        this.stopUpdate = this.stopUpdate.bind(this);
        this.onEffDateChange = this.onEffDateChange.bind(this);
        this.getTimeStamp = this.getTimeStamp.bind(this);

        this.state = {
            vehicleChanged: false,
            coveragesChanged: false,
            driverChanged: false,
            violationsChanged: false,
            updatingStore: false,
            bindQuoteResult: null,
            errors: null,
            paymentChoice: null,
            externalQuote: this.props.externalQuote,
            rerating: false,
            effDateChanging: false,
            effectiveDatetimeStamp: this.getTimeStamp(this.props.externalQuote, new Date(this.props.externalQuote.effectiveDate))
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        let _vehicleChanged = false;
        let _driverChanged = false;
        let _coveragesChanged = false;
        let _violationsChanged = false;

        for (let i = 0; i < this.props.externalQuote.vehicles.length; i++) {
            if (this.props.externalQuote.vehicles[i].changed) {
                _vehicleChanged = true;
                break;
            }
        }

        for (let i = 0; i < this.props.externalQuote.drivers.length; i++) {
            if (this.props.externalQuote.drivers[i].driverChanged) {
                _driverChanged = true;
                break;
            }
        }

        if (this.props.externalQuote.coverages?.changed) {
            _coveragesChanged = true;
        }

        if (!this.props.externalQuote.agreement?.agreePolicyEffDate) {
            this.props.reSetQuoteTimeStampEffDate(this.state.effectiveDatetimeStamp);
        }

        if (this.props.mvrResult?.hasViolationData) {
            _violationsChanged = true;
        }

        this.setState({ ...this.state, vehicleChanged: _vehicleChanged, driverChanged: _driverChanged, violationsChanged: _violationsChanged, coveragesChanged: _coveragesChanged });
    }

    componentWillUnmount() {
        this.props.resetChangedFlags();
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            nextProps.meta.propsSource = "";
            
            return {
                ...prevState,
                paymentChoice: nextProps.paymentChoice
            };
        }

        return prevState;
    }

    stopUpdate() {
        this.setState({ ...this.state, updatingStore: false, rerating: false, effDateChanging: false });
    }

    onEffDateChange(date: Date | null) {
        if (date) {
            const _effectiveDatetimeStamp = this.getTimeStamp(this.state.externalQuote, date);

            this.setState({ updatingStore: true, rerating: true, effDateChanging: false }, () => {
                this.props.reSetQuoteEffDate(this.props.externalQuote.rtrQuoteId, this.props.externalQuote.paymentPlanCode, date.toDateString(), _effectiveDatetimeStamp)
                    .then(() => {
                        this.setState({
                            ...this.state,
                            updatingStore: false,
                            rerating: false,
                            effDateChanging: true,
                            effectiveDatetimeStamp: _effectiveDatetimeStamp
                        });
                    }, () => { this.stopUpdate(); });
            });
        }
    }

    showErrors(errors: Array<string> | undefined) {
        if (!errors) return null;
        return <Alert variant={"danger"}>
            <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
            <p>Please review errors below</p>
            <ul>
                {
                    errors.map((error) => (
                        <li key={Utilities.uniqueId()}>
                            <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                        </li>
                    ))
                }
            </ul>
        </Alert>;
    }

    getTimeStamp(externalQuote: IExternalQuote, date: Date)
    {
        if (externalQuote.agreement?.agreePolicyEffDate)
        {
            return externalQuote.effectiveDatetimeStamp;
        }
        else
        {
            var csttm = moment().tz("America/Chicago").format('h:mm a z');

            var diff = moment(new Date()).diff(date);

            if (diff < 0) {
                var zone = moment().tz("America/Chicago").zoneAbbr();
                csttm = "12:01 am " + zone;
            }

            return csttm;
        }
    }

    next() {
        this.setState({ updatingStore: true, bindQuoteResult: null }, () => {
            this.props.history.push(this.props.PATHS.FQ_QUESTIONS);
        });
    }

    render() {
        return (
            <QuoteStep id="policyDetails" className="policy-details">
                <div className="titleArea">
                    <h5>Policy Details</h5>
                </div>

                {this.showErrors(this.state.bindQuoteResult?.errors)}

                {/*{this.state.rerating*/}
                {/*    ?*/}
                {/*    <Alert variant={"warning"}>*/}
                {/*        Re-rating quote. Please wait...*/}
                {/*    </Alert>*/}
                {/*    : null*/}
                {/*}*/}

                {/*{this.state.vehicleChanged*/}
                {/*    ? <Alert variant={"warning"}>*/}
                {/*        Your vehicle information has changed based on the VIN provided. Please see adjusted premiums below.*/}
                {/*      </Alert>*/}
                {/*    : null*/}
                {/*}*/}

                {/*{this.state.coveragesChanged*/}
                {/*    ? <Alert variant={"warning"}>*/}
                {/*        Your coverages information has changed. Please see adjusted premiums below.*/}
                {/*    </Alert>*/}
                {/*    : null*/}
                {/*}*/}

                {/*{this.state.driverChanged*/}
                {/*    ? <Alert variant={"warning"}>*/}
                {/*         Your driver information has changed. Please see adjusted premiums below.*/}
                {/*    </Alert>*/}
                {/*    : null*/}
                {/*}*/}

                {/*{this.state.violationsChanged*/}
                {/*    ? <Alert variant={"warning"}>*/}
                {/*        A violation report has been ordered. Based on the data received, the premiums may change.*/}
                {/*    </Alert>*/}
                {/*    : null*/}
                {/*}*/}

                {/*{this.state.effDateChanging*/}
                {/*    ? <Alert variant={"warning"}>*/}
                {/*        Your policy effective date has changed. Please see adjusted premiums below.*/}
                {/*      </Alert>*/}
                {/*    : null*/}
                {/*}*/}

                <p className="info">Great; we are just about finished! Please review the details of the policy below: </p>

                <div className="payment-choice">
                    <div className="item">
                        <div className="left-cell eff-date-details">
                            <Calendar
                                id="effectiveDate"
                                value={new Date(this.state.externalQuote.effectiveDate)}
                                minDate={today}
                                maxDate={moment(new Date()).add(30, 'd').toDate()}
                                validated={null !== this.state.errors}
                                validationError={validationUtils.getValidationErrorFor(this.state.errors, "effectiveDate")}
                                onChange={this.onEffDateChange}
                                disabled={this.state.externalQuote.agreement?.agreePolicyEffDate}
                            />
                        </div>
                        <label className="right-cell">Effective date</label>
                    </div>
                    {/*<div className="item">*/}
                    {/*    <label className="left-cell" style={{ fontSize: "14pt" }}>{this.state.effectiveDatetimeStamp}</label>*/}
                    {/*    <label className="right-cell">Time stamp</label>*/}
                    {/*</div>*/}
                    {/*<p className="info" style={{ fontStyle: "italic" }}>* Your car insurance policy will go into effect from the moment you purchase it </p>*/}
                    <div className="item">
                        <label className="left-cell">{Utilities.formatCurrency(this.state.paymentChoice?.firstPay)}</label>
                        <label className="right-cell">Due today</label>
                    </div>
                    {this.state.paymentChoice?.payCount && this.state.paymentChoice?.payCount > 1
                        ?
                            <div className="item">
                            {
                                this.state.paymentChoice?.payTermCd === 'MONTHLY'
                                    ?
                                    <div>
                                        <label className="left-cell">{Utilities.formatCurrency(this.state.paymentChoice?.regularPay)}</label>
                                        {
                                            this.state.paymentChoice?.autopay
                                                ? <label className="right-cell">Monthly starting on {this.state.externalQuote.installmentDueDate} autopay</label>
                                                : <label className="right-cell">Monthly starting on {this.state.externalQuote.installmentDueDate}</label>
                                        }
                                    </div>
                                    :
                                    null
                            }
                            {
                                this.state.paymentChoice?.payTermCd === 'BI-MONTHLY'
                                    ?
                                    <div>
                                        <label className="left-cell">{Utilities.formatCurrency(this.state.paymentChoice?.regularPay)}</label>
                                        {
                                            this.state.paymentChoice?.autopay
                                                ? <label className="right-cell"> Bi-monthly (every 15 days) starting on {this.state.externalQuote.installmentDueDate} autopay</label>
                                                : <label className="right-cell"> Bi-monthly (every 15 days) starting on {this.state.externalQuote.installmentDueDate}</label>
                                        }
                                    </div>
                                    :
                                    null
                            }
                            </div>
                        : null
                    }
                </div>

           {/*     <div className="info-disclosure quote-info">*/}
           {/*         <div className="item">*/}
           {/*             <label className="left-cell">Policy Coverage Start Date</label>*/}
           {/*             <label className="right-cell">{this.state.externalQuote.effectiveDate}</label>*/}
           {/*         </div>*/}
           {/*         <div className="item">*/}
           {/*             <label className="left-cell">Named Insured</label>*/}
           {/*             <label className="right-cell">{this.props.externalQuote.insured.firstName + " " + this.props.externalQuote.insured.lastName}</label>*/}
           {/*         </div>*/}
           {/*     </div>*/}

           {/*     {this.props.externalQuote.vehicles.map((v) => (*/}
           {/*         <VehicleCoverages*/}
           {/*             key={Utilities.uniqueId()}*/}
           {/*             vehicle={v} />*/}
           {/*     ))}*/}

           {/*     {this.state.paymentChoice?.motorclubFee && this.state.paymentChoice?.motorclubFee > 0*/}
           {/*         ?*/}
           {/*         <div className="vehicle-coverages">*/}
           {/*             <div className="break-2" />*/}
           {/*             <div className="titleArea">*/}
           {/*                 <h5>Addons</h5>*/}
           {/*             </div>*/}
           {/*             <div className="info-disclosure quote-info">*/}
           {/*                 <div className="coverage" key={Utilities.uniqueId()}>*/}
           {/*                     <div className="coverage-name" style={{ paddingTop: "2%" }}>*/}
           {/*                         <div>Roadside Assistance</div>*/}
           {/*                     </div>*/}
           {/*                     <div className="row item no-border" style={{ paddingBottom: "2%" }} key={Utilities.uniqueId()}>*/}
           {/*                         <div className="col-6">*/}
           {/*                             <strong>additional $9 monthly</strong>*/}
           {/*                         </div>*/}
           {/*                         <div className="col-6">*/}
           {/*                             <div className="float-right">*/}
           {/*                                 <strong>{Utilities.formatCurrency(this.state.paymentChoice?.motorclubFee)}</strong>*/}
           {/*                             </div>*/}
           {/*                         </div>*/}
           {/*                     </div>*/}
           {/*                 </div>*/}
           {/*             </div>*/}
			        {/*</div>*/}
           {/*         :*/}
           {/*         null*/}
           {/*     }*/}

                <div className="btnContainer">
                    <ButtonContinue
                        label={"Continue"}
                        className="btn-sign"
                        disabled={this.state.updatingStore}
                        showLoading={this.state.updatingStore}
                        onClick={this.next} />
                </div>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        coverageConfig: state.coverageConfig,
        paymentChoice: state.paymentChoice,
        externalQuote: state.externalQuote,
        mvrResult: state.externalQuote?.mvrResult,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(FULL_QUOTE_STEPS.POLICY_DETAILS)),
        bindQuote: (quoteId: number) => dispatch(fullQuoteActions.bindQuote(quoteId)),
        reSetQuoteEffDate: (quoteId: number, paymentPlanCode: number, effectiveDate: string, effectiveDatetimeStamp: string) => dispatch(fullQuoteActions.reSetQuoteEffDate(quoteId, paymentPlanCode, effectiveDate, effectiveDatetimeStamp)),
        reSetQuoteTimeStampEffDate: (effectiveDatetimeStamp: string) => dispatch(fullQuoteActions.reSetQuoteTimeStampEffDate(effectiveDatetimeStamp)),
        resetChangedFlags: () => dispatch(fullQuoteActions.resetChangedFlags())
    };
};

export default connect(mapProps, mapDispatch)(PolicyDetails);
